import { useMemo, useState } from 'react';

import type { ChartData, ChartFields } from '../../types';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';

/**
 * Detail and exact values.
 *
 * This is also every other chart's accessibility twin: `ChartFrame` renders it
 * behind a toggle for any form, which is what lets a chart use a colour that
 * sits below 3:1 against the surface, or push a value that wouldn't fit on a
 * mark into a tooltip. Nothing in this app is readable *only* by hovering.
 */

interface Props {
  data: ChartData;
  /** Rows to render. The table view of a large result is paged by the caller. */
  limit?: number;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const DEFAULT_LIMIT = 500;

type SortState = { column: number; direction: 'asc' | 'desc' } | null;

const compare = (a: unknown, b: unknown): number => {
  if (isNumeric(a) && isNumeric(b)) return a - b;
  if (a === null || a === undefined || a === '') return -1;
  if (b === null || b === undefined || b === '') return 1;
  return String(a).localeCompare(String(b), 'en-GB', { numeric: true });
};

export const DataTable = ({ data, limit = DEFAULT_LIMIT }: Props) => {
  const [sort, setSort] = useState<SortState>(null);

  const numericColumns = useMemo(
    () =>
      data.columns.map((_, i) =>
        data.rows.slice(0, 30).some((row) => isNumeric(row[i])),
      ),
    [data],
  );

  const rows = useMemo(() => {
    const slice = data.rows.slice(0, limit);
    if (!sort) return slice;

    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...slice].sort((a, b) => sign * compare(a[sort.column], b[sort.column]));
  }, [data, limit, sort]);

  if (!data.rows.length) return <Empty />;

  const toggleSort = (column: number) =>
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === 'desc' ? 'asc' : 'desc' }
        : { column, direction: 'desc' },
    );

  return (
    <div className="data-table">
      <table>
        <thead>
          <tr>
            {data.columns.map((column, i) => (
              <th
                key={column}
                className={numericColumns[i] ? 'num' : undefined}
                aria-sort={
                  sort?.column === i
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                <button type="button" onClick={() => toggleSort(i)}>
                  {humanise(column)}
                  <span className="data-table__sort" aria-hidden="true">
                    {sort?.column === i ? (sort.direction === 'asc' ? '↑' : '↓') : ''}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((value, j) => (
                <td key={j} className={numericColumns[j] ? 'num' : undefined}>
                  {cell(value, 'exact')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {data.rows.length > limit && (
        <p className="data-table__note">
          Showing the first {limit.toLocaleString('en-GB')} of{' '}
          {data.rows.length.toLocaleString('en-GB')} rows.
        </p>
      )}
    </div>
  );
};
